import "./Home.css";
import React, { useState, useEffect, memo } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { cn } from "lib/utils";

import Filter from "../filter";
import Login from "../login";
import Health from "../health";
import { Actions } from "../actions";
import RequestUrls from "../requestUrls";
import Resources from "../resources";
import StatusCodeView from "../statusCodeView";
import EventDetails from "../eventDetails";
import { isInvalidTimeRange } from "./timeRange";
import useGetEventDetailsApi from "../../hooks/useGetEventDetails";

const MemoizedHealth = memo(Health);
const MemoizedActions = memo(Actions);
const MemoizedRequestUrls = memo(RequestUrls);
const MemoizedResource = memo(Resources);
const MemoizedStatusCodeView = memo(StatusCodeView);
const MemoizedEventDetails = memo(EventDetails);

const COLUMN_OPTIONS = [1, 2, 3, 4];

function ErrorAlert({ error }) {
    return (
        <div className="mx-4 my-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <strong>Warning!</strong> {error}
        </div>
    );
}

function Home() {
    var { eventId, environment, startTime, endTime, duration } = getParams();
    const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
    const [columns, setColumns] = useState(() => {
        const saved = parseInt(localStorage.getItem("healthColumns"), 10);
        return saved >= 1 && saved <= 4 ? saved : 3;
    });

    const { eventDetails, error, isLoaded, refetchEventDetails } = useGetEventDetailsApi(
        eventId,
        environment
    );

    // Capture range validity from the raw params before the block below reassigns duration.
    const invalidTimeRange = isInvalidTimeRange(startTime, endTime, duration);

    if (startTime && endTime && !duration) {
        duration = "Range";
    } else {
        duration = duration ? duration.substring(2) : "1H";
        const durationTime = moment.duration(`PT${duration}`);
        const currentTime = moment.utc();
        endTime = currentTime.format("YYYY-MM-DDTHH:mm");
        const newTime = currentTime.subtract(durationTime);
        startTime = newTime.format("YYYY-MM-DDTHH:mm");
    }

    const toggleTheme = () => {
        const newTheme = theme == "light" ? "dark" : "light";
        setTheme(newTheme);
    };

    const tabs = [
        {
            title: "Health",
            tab_url: "health",
            content: (
                <MemoizedHealth
                    eventId={eventId}
                    environment={environment}
                    startTime={startTime}
                    endTime={endTime}
                    theme={theme}
                    columns={columns}
                />
            ),
        },
        {
            title: "Cache Requests",
            tab_url: "cacheRequests",
            content: (
                <MemoizedRequestUrls
                    eventId={eventId}
                    environment={environment}
                    startTime={startTime}
                    endTime={endTime}
                    theme={theme}
                />
            ),
        },
        {
            title: "Origin Live Requests",
            tab_url: "originLiveRequests",
            content: (
                <MemoizedRequestUrls
                    eventId={eventId}
                    environment={environment}
                    startTime={startTime}
                    endTime={endTime}
                    theme={theme}
                    logSource="origin-live"
                />
            ),
        },
        {
            title: "Actions",
            tab_url: "actions",
            content: (
                <MemoizedActions
                    eventId={eventId}
                    environment={environment}
                    eventDetails={eventDetails}
                />
            ),
        },
        {
            title: "Resources",
            tab_url: "resources",
            content: <MemoizedResource eventId={eventId} environment={environment} theme={theme} />,
        },
        {
            title: "Status Code Stats",
            tab_url: "statusCode",
            content: (
                <MemoizedStatusCodeView
                    eventId={eventId}
                    environment={environment}
                    startTime={startTime}
                    endTime={endTime}
                    theme={theme}
                />
            ),
        },
        {
            title: "Event Details",
            tab_url: "eventDetails",
            content: <MemoizedEventDetails eventId={eventId} environment={environment} />,
        }
    ];
    // Keep Alive is now a card on the Actions tab; send its old links there.
    const requestedTabUrl = window.location.hash.slice(1);
    const [activeTabUrl, setactiveTabUrl] = useState(
        requestedTabUrl === "keepAlive" ? "actions" : requestedTabUrl
    );
    const initialActiveTab = tabs.findIndex((tab) => tab.tab_url === activeTabUrl);
    const [activeTab, setActiveTab] = useState(initialActiveTab !== -1 ? initialActiveTab : 0);

    const [tabsLoaded, setTabsLoaded] = useState(Array(tabs.length).fill(false));

    const markTabLoaded = (tabIndex) => {
        setTabsLoaded((prevTabsLoaded) => {
            const newTabsLoaded = [...prevTabsLoaded];
            newTabsLoaded[tabIndex] = true;
            return newTabsLoaded;
        });
    };

    useEffect(() => {
        if (!tabsLoaded[activeTab]) {
            setactiveTabUrl(tabs[activeTab].tab_url);
            markTabLoaded(activeTab);
        }
    }, [activeTab, tabsLoaded]);

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme === "dark");
        localStorage.setItem("theme", theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("healthColumns", String(columns));
    }, [columns]);

    useEffect(() => {
        if (invalidTimeRange) {
            toast.error("End time needs to be greater than start time");
        }
    }, [invalidTimeRange]);

    const handleTabClick = (tabIndex) => {
        setActiveTab(tabIndex);
    };

    if (localStorage.getItem("credential") === null) {
        return <Login />;
    }

    return (
        <div className="home_class min-h-screen bg-background text-foreground">
            <Filter
                eventId={eventId}
                environment={environment}
                duration={duration}
                startTime={startTime}
                endTime={endTime}
                eventDetails={eventDetails}
                theme={theme}
                toggleTheme={toggleTheme}
                activeTabUrl={activeTabUrl}
            />
            <div className="px-4 pb-6 sm:px-6">
                {!error ? getTabManager() : <ErrorAlert error={error} />}
            </div>
        </div>
    );

    function getParams() {
        const searchParams = new URLSearchParams(window.location.search);
        let searchParamsDict = Object.fromEntries(searchParams.entries());

        const eventId = searchParamsDict["event_id"];
        const environment = searchParamsDict["environment"];
        let startTime = searchParamsDict["startTime"];
        let endTime = searchParamsDict["endTime"];
        let duration = searchParamsDict["duration"];
        return { eventId, environment, startTime, endTime, duration };
    }

    function getTabManager() {
        return (
            <div className="mt-4">
                <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1 shadow-sm">
                    {tabs.map((tab, index) => (
                        <a
                            key={index}
                            onClick={() => handleTabClick(index)}
                            href={`#${tab.tab_url}`}
                            className={cn(
                                "cursor-pointer rounded-md px-3.5 py-2 text-sm font-medium transition-colors",
                                activeTab === index
                                    ? "bg-primary text-primary-foreground shadow-sm"
                                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                        >
                            {tab.title}
                        </a>
                    ))}
                    {tabs[activeTab].tab_url === "health" && (
                        <div className="ml-auto flex items-center gap-2 pr-1">
                            <span className="text-sm text-muted-foreground">Columns</span>
                            <div className="inline-flex rounded-md border border-border p-0.5">
                                {COLUMN_OPTIONS.map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={cn(
                                            "rounded px-3 py-1 text-sm font-medium transition-colors",
                                            n === columns
                                                ? "bg-primary text-primary-foreground"
                                                : "text-muted-foreground hover:text-foreground"
                                        )}
                                        onClick={() => setColumns(n)}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="pt-4">
                    {tabs.map((tab, index) => (
                        <div key={index} className={activeTab === index ? "block" : "hidden"}>
                            {tabsLoaded[index] ? tab.content : null}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default Home;
